import { json } from "react-router-dom";

import { MESSAGE_COLlECTION } from "@fb/const";
import { db } from "@fb/db";
import { s } from "@fullcalendar/core/internal-common";
import { useMutation } from "@tanstack/react-query";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";

import { useAppDispatch } from "@app/hooks";
import { AppDispatch } from "@app/store";

import { axios } from "@features/config";
import { JobFields, setJobFields } from "@features/jobManagement/slice";
import { addMessage, updateMessage } from "@features/messages/slice";

import {
  addNewMessage,
  findOrCreateNewConversation,
} from "@components/Conversation/service";

import { removeEmptyFields } from "@utils/index";

import { useGawainAssistant } from "@contexts/GawainAssistantContext";

import { ENV } from "@config/env";

interface PostStreamPayload {
  text: string;
  fields: any;
  userName: string;
  userId: string;
  avatar: string;
  imageUrls?: { name: string; url: string }[];
  fileUrls?: { name: string; url: string }[];
  conversationId?: string;
}

const stripHtmlTags = (text: string): string => {
  return text.replace(/<[^>]*>/g, "");
};

const postStreamedData = async (payload: PostStreamPayload, dispatch: AppDispatch) => {
  const formattedMessages: any[] = [{ type: "text", text: payload.text }];

  const formattedMessage = {
    message: {
      role: "user",
      content: formattedMessages,
      title: payload.userName,
      avatar: payload.avatar,
      imageUrls: payload.imageUrls,
      fileUrls: payload.fileUrls,
    },
    userId: payload.userId,
    conversationId: payload.conversationId,
  };

  // Reset the session if conversation exists, or create a new one if not
  await findOrCreateNewConversation(payload.conversationId);

  // Add user messages to Firebase
  await addNewMessage(formattedMessage as any);

  const response: any = await fetch(ENV.REACT_APP_STREAM_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      userId: payload.userId,
      messages: stripHtmlTags(payload.text),
      fields: payload.fields,
      userName: payload.userName,
      avatar: payload.avatar,
      files: [...(payload.imageUrls || []), ...(payload.fileUrls || [])],
      conversationId: payload.conversationId,
    }),
  });

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No reader available");
  }

  const decoder = new TextDecoder();
  let accumulatedContent = "";
  let isFirstChunk = true;
  const messageId = uuidv4();
  let jsonDispatched = false;
  let parsedJobFields: JobFields | null = null;
  let formattedJsonContent: string | null = null;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    const chunk = decoder.decode(value, { stream: true });
    accumulatedContent += chunk;

    // Check for JSON match
    const jsonMatch = accumulatedContent.match(/\{[\s\S]*\}/);
    if (jsonMatch && !jsonDispatched) {
      try {
        let parsedContent = JSON.parse(jsonMatch[0]) as JobFields;
        // Remove empty/null fields
        parsedContent = removeEmptyFields(parsedContent);
        // Store parsed JSON and formatted content for later
        parsedJobFields = { ...parsedContent };
        formattedJsonContent = Object.entries(parsedContent || {})
          .map(([key, value]) => {
            return `</br><b>${key.toLocaleUpperCase()}</b>: ${JSON.stringify(value)}`;
          })
          .join("\n");
        // Dispatch setJobFields with pending status
        console.log("parsedJobFields", parsedJobFields);

        dispatch(setJobFields({ ...parsedJobFields }));
        jsonDispatched = true;
        continue; // Do not update message yet
      } catch (e) {
        // If JSON parsing fails, fall through to normal update
      }
    }

    if (isFirstChunk) {
      dispatch(
        addMessage({
          conversationId: payload.conversationId,
          message: {
            id: messageId,
            title: payload.userName,
            userId: payload.userId,
            date: Date.now(),
            content: accumulatedContent,
            role: "assistant",
          },
        })
      );
      isFirstChunk = false;
    } else {
      dispatch(
        updateMessage({
          conversationId: payload.conversationId,
          messageId,
          content: accumulatedContent,
        })
      );
      jsonDispatched = false;
    }
  }

  if (parsedJobFields && formattedJsonContent) {
    dispatch(setJobFields({ ...parsedJobFields }));
    // Wait 3 seconds before updating the message
    await new Promise((res) => setTimeout(res, 3000));
    // Animate only the formatted JSON part word by word
    const jsonMatch = accumulatedContent.match(/\{[\s\S]*\}/);

    if (jsonMatch) {
      // Prepare for animation
      const before = accumulatedContent.slice(0, jsonMatch.index);
      const after = accumulatedContent.slice(jsonMatch.index + jsonMatch[0].length);
      const words = formattedJsonContent.split(/(\s+)/); // keep spaces

      accumulatedContent = accumulatedContent.replace(
        jsonMatch[0],
        `</br>${formattedJsonContent}`
      );
      let animatedJson = "";
      for (let i = 0; i < words.length; i++) {
        animatedJson += words[i];
        const animatedMessage = before + animatedJson + after;

        dispatch(
          updateMessage({
            conversationId: payload.conversationId,
            messageId,
            content: animatedMessage,
          })
        );
        // Wait 40ms between each word
        // eslint-disable-next-line no-await-in-loop
        // await new Promise((res) => setTimeout(res, 40));
      }
    }
  }

  try {
    const messageRef = doc(db, MESSAGE_COLlECTION, messageId);
    await setDoc(messageRef, {
      id: messageId,
      title: payload.userName,
      userId: payload.userId,
      date: serverTimestamp(),
      fileUrls: payload.fileUrls,
      imageUrls: payload.imageUrls,
      content: accumulatedContent,
      role: "assistant",
      conversationId: payload.conversationId,
    });
  } catch (error) {
    console.error("Error saving message to Firestore:", error);
  }

  return { data: accumulatedContent, success: true };
};

const useAssistantChat = () => {
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: (payload: PostStreamPayload) => postStreamedData(payload, dispatch),
  });
};

export default useAssistantChat;
